import React, { useEffect, useMemo, useState } from "react";
import {
    AlertCircle,
    FileText,
    ImageOff,
    Loader2,
    Mail,
    MessageCircle,
    Search,
} from "lucide-react";
import { toast } from "sonner";
import { PageHero } from "./About";
import { COMPANY } from "../data/site";

const PRODUCTS_API_URL = "https://admin.vilengineering.com/api/public/products";

const normalizeProductsResponse = (payload) => {
    const rawProducts = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.products)
            ? payload.products
            : Array.isArray(payload?.data)
                ? payload.data
                : [];

    return rawProducts.map((product, index) => {
        const specificationItems = Array.isArray(product.specificationItems)
            ? product.specificationItems.filter(Boolean)
            : [];
        const specText =
            product.specifications ||
            product.spec ||
            specificationItems.join(" | ") ||
            "Contact VIL Engineering for technical specifications.";

        return {
            id: product.id || product.slug || `product-${index}`,
            name: product.name || product.title || "VIL Engineering Product",
            slug: product.slug || product.id || `product-${index}`,
            category: product.category || "Fire Safety Products",
            description:
                product.description ||
                product.metaDescription ||
                "Industrial fire safety product supplied by VIL Engineering.",
            spec: specText,
            specificationItems,
            image:
                product.imageUrl ||
                product.image ||
                product.thumbnailUrl ||
                product.thumbnail ||
                "",
            datasheetUrl: product.datasheetUrl || "",
            url: product.url || "",
            sortOrder: Number.isFinite(Number(product.sortOrder))
                ? Number(product.sortOrder)
                : index,
            updatedAt: product.updatedAt || "",
        };
    }).sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
};

const getCategories = (apiCategories, products) => {
    const categories = Array.isArray(apiCategories) ? apiCategories : [];
    const merged = new Set([
        "All",
        ...categories.filter(Boolean),
        ...products.map((product) => product.category).filter(Boolean),
    ]);
    return Array.from(merged);
};

const Shop = () => {
    const [category, setCategory] = useState("All");
    const [query, setQuery] = useState("");
    const [products, setProducts] = useState([]);
    const [apiCategories, setApiCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        const controller = new AbortController();

        const loadProducts = async () => {
            setLoading(true);
            setError("");

            try {
                const response = await fetch(PRODUCTS_API_URL, {
                    headers: { Accept: "application/json" },
                    signal: controller.signal,
                });

                if (!response.ok) {
                    throw new Error(`Catalog request failed with ${response.status}`);
                }

                const payload = await response.json();
                const normalized = normalizeProductsResponse(payload);

                setProducts(normalized);
                setApiCategories(payload?.categories || []);
                if (!normalized.length) {
                    setError("No live products are available right now.");
                }
            } catch (err) {
                if (err.name === "AbortError") return;
                setProducts([]);
                setApiCategories([]);
                setError("Live product catalog is temporarily unavailable.");
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        loadProducts();

        return () => controller.abort();
    }, []);

    const categories = useMemo(
        () => getCategories(apiCategories, products),
        [apiCategories, products]
    );

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();

        return products.filter((product) => {
            const matchCat = category === "All" || product.category === category;
            const searchable = [
                product.name,
                product.category,
                product.description,
                product.spec,
                ...product.specificationItems,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            return matchCat && (!q || searchable.includes(q));
        });
    }, [category, products, query]);

    return (
        <>
            <PageHero
                eyebrow="Products"
                title="Fire safety products supplied by VIL Engineering."
                subtitle="Browse products listed by the VIL Engineering admin team. Send enquiries by WhatsApp or email for pricing, availability, datasheets, and bulk supply."
            />

            <section className="section pt-12" data-testid="products-section">
                <div className="container-vil">
                    <div className="flex flex-col lg:flex-row gap-4 lg:items-center justify-between mb-8">
                        <div>
                            <span className="eyebrow">/ Live Product Catalog</span>
                        </div>

                        <button
                            onClick={() => {
                                const subject = encodeURIComponent("Product Enquiry - VIL Engineering");
                                window.location.href = `mailto:${COMPANY.primaryEmail}?subject=${subject}`;
                                toast.success("Opening email client...");
                            }}
                            className="btn-secondary"
                            data-testid="products-bulk-enquiry"
                        >
                            <FileText className="w-4 h-4" />
                            Bulk Enquiry
                        </button>
                    </div>

                    <div className="flex flex-col lg:flex-row gap-4 lg:items-center justify-between mb-8">
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                            <input
                                type="text"
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                placeholder="Search products, categories, specs..."
                                className="w-full bg-slate-50 border border-slate-200 text-slate-900 pl-11 pr-4 py-3 rounded-sm focus:border-[#E53935] focus:outline-none focus:ring-1 focus:ring-[#E53935] focus:bg-white transition placeholder:text-slate-400"
                                data-testid="products-search"
                            />
                        </div>

                        {loading && (
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Loading latest products
                            </div>
                        )}
                    </div>

                    {error && (
                        <div
                            className="mb-8 flex items-start gap-3 border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
                            data-testid="products-api-warning"
                        >
                            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="flex gap-2 overflow-x-auto pb-3 mb-10 border-b border-slate-200">
                        {categories.map((item) => (
                            <button
                                key={item}
                                onClick={() => setCategory(item)}
                                className={`px-5 py-2.5 text-sm font-mono uppercase tracking-widest whitespace-nowrap transition border ${
                                    category === item
                                        ? "bg-[#E53935] border-[#E53935] text-slate-900"
                                        : "bg-transparent border-slate-200 text-slate-600 hover:border-slate-400 hover:text-slate-900"
                                }`}
                                data-testid={`products-category-${item.replace(/\s/g, "-").toLowerCase()}`}
                            >
                                {item}
                            </button>
                        ))}
                    </div>

                    {filtered.length === 0 ? (
                        <div className="text-center py-20 text-slate-500" data-testid="products-empty">
                            {products.length === 0
                                ? "No products are available right now."
                                : "No products match your search."}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                            {filtered.map((product) => (
                                <ProductCard key={product.id} product={product} />
                            ))}
                        </div>
                    )}

                    <p className="text-xs text-slate-500 font-mono mt-12 text-center max-w-2xl mx-auto">
                        Prices depend on quantity, location, brand, and certification needs.
                        Send an enquiry and the VIL Engineering team will respond with a
                        suitable quote.
                    </p>
                </div>
            </section>
        </>
    );
};

const ProductCard = ({ product }) => {
    const [imageError, setImageError] = useState(false);
    const productDetails = [
        product.description,
        product.spec,
        ...product.specificationItems,
    ]
        .filter(Boolean)
        .join(" | ");

    const enquiryText = encodeURIComponent(
        `Hello VIL Engineering, I want to enquire about ${product.name}. Category: ${product.category}. Please share price, availability, and details.`
    );

    const emailSubject = encodeURIComponent(`Product Enquiry: ${product.name}`);
    const emailBody = encodeURIComponent(
        `Hello VIL Engineering,\n\nI want to enquire about this product:\n\nProduct: ${product.name}\nCategory: ${product.category}\nDetails: ${productDetails}\n\nPlease share price, availability, and technical details.\n`
    );

    return (
        <article
            className="group glass corner-accent relative overflow-hidden card-lift flex flex-col"
            data-testid={`product-card-${product.id}`}
        >
            <div className="relative h-56 overflow-hidden bg-slate-100">
                {product.image && !imageError ? (
                    <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover img-zoom"
                        loading="lazy"
                        onError={() => setImageError(true)}
                    />
                ) : (
                    <div className="h-full w-full flex flex-col items-center justify-center gap-2 text-slate-400">
                        <ImageOff className="w-8 h-8" />
                        <span className="text-xs font-mono uppercase tracking-widest">
                            Image unavailable
                        </span>
                    </div>
                )}
                <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition"
                    style={{
                        background:
                            "linear-gradient(180deg, transparent 50%, rgba(229,57,53,0.25) 100%)",
                    }}
                />
            </div>

            <div className="p-5 flex-1 flex flex-col">
                <div className="text-xs font-mono text-[#E53935] uppercase tracking-widest mb-2">
                    {product.category}
                </div>
                <h3 className="font-display font-bold text-slate-900 text-base mb-2 leading-snug">
                    {product.name}
                </h3>
                <p className="text-sm text-slate-700 mb-3 leading-relaxed">
                    {product.description}
                </p>

                <div className="mb-5 flex-1">
                    <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">
                        Product Information
                    </div>
                    {product.specificationItems.length > 0 ? (
                        <ul className="space-y-1.5">
                            {product.specificationItems.slice(0, 4).map((item) => (
                                <li key={item} className="text-xs text-slate-600 leading-relaxed">
                                    {item}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-xs text-slate-600 leading-relaxed">{product.spec}</p>
                    )}
                </div>

                <div className="flex flex-col gap-2 mt-auto">
                    <a
                        href={`https://wa.me/${COMPANY.whatsapp}?text=${enquiryText}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary justify-center !py-2.5 text-sm"
                        data-testid={`product-whatsapp-${product.id}`}
                    >
                        <MessageCircle className="w-4 h-4" />
                        WhatsApp Enquiry
                    </a>
                    <a
                        href={`mailto:${COMPANY.primaryEmail}?subject=${emailSubject}&body=${emailBody}`}
                        className="btn-secondary justify-center !py-2.5 text-sm"
                        data-testid={`product-email-${product.id}`}
                    >
                        <Mail className="w-4 h-4" />
                        Email Enquiry
                    </a>
                </div>
            </div>
        </article>
    );
};

export default Shop;
